import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RowSelectionState } from "@tanstack/react-table";
import { useQueryClient } from "@tanstack/react-query";
import { parseISO, startOfDay } from "date-fns";
import { ServiceTemplateSchedule } from "@common/types/serviceTemplateSchedule";
import { ServiceTemplateRecurrenceStatus } from "@common/types/serviceTemplateRecurrence";
import { FiltersTypes } from "@common/types/filters/general";
import { ServiceTemplateRecurrenceOccurrenceStatus } from "@common/types/serviceTemplateRecurrenceOccurrence";
import { ServiceTemplateScheduleOccurrencesSortValue } from "@common/types/views/serviceTemplateScheduleOccurrencesView";
import { useOpenWorkRow } from "@hooks/useOpenWorkRow";
import { Drawer } from "@design-system/Drawer/Drawer";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { Loader } from "@design-system/Loader/Loader";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { useScope } from "@state/scopeContext.lib";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import { useCurrentOrganizationMembers } from "@api/organizationMember.api";
import { useCloseTemplates } from "@api/closeTemplate.api";
import { useWorkspaceDetails } from "@api/workspace.api";
import { useWorkspaceEntities } from "@hooks/useWorkspaceEntities";
import { WorkspaceAvatar } from "@design-system/Avatar/WorkspaceAvatar";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import {
    BreadcrumbItem,
    Breadcrumbs,
} from "@design-system/Breadcrumbs/Breadcrumbs";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { BulkSelectionControl } from "@design-system/BulkSelectionControl/BulkSelectionControl";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { Pagination } from "@design-system/Pagination/Pagination";
import { Shortcut } from "@design-system/Shortcut/Shortcut";
import { pluralize } from "@common/helpers/string";
import { useDeleteCloseMutation } from "@api/close.api";
import {
    useApplyOccurrenceNowMutation,
    useServiceTemplateScheduleOccurrences,
    useSkipOccurrenceMutation,
    useUnskipOccurrenceMutation,
    useUpdateOccurrenceRoleOverrideMutation,
} from "@api/serviceTemplateScheduleOccurrence.api";
import { serviceTemplateScheduleQueryKeys } from "@api/serviceTemplateSchedule.api";
import {
    FilterTabs,
    FiltersChangeFn,
} from "@components/pageHeader/filters/lib";
import type {
    ServiceTemplateScheduleOccurrenceCallbacks,
    ServiceTemplateScheduleOccurrenceRoleField,
    ServiceTemplateScheduleOccurrenceTableOptions,
} from "./serviceTemplateScheduleOccurrenceCallbacks";
import type { ServiceTemplateScheduleOccurrenceRow } from "./serviceTemplateScheduleOccurrencesViewConfig";
import { ServiceTemplateScheduleOccurrencesTable } from "./ServiceTemplateScheduleOccurrencesTable";
import { serviceTemplateScheduleOccurrencesDrawerView } from "./serviceTemplateScheduleOccurrencesView.constants";
import { useServiceTemplateScheduleOccurrenceBulkActions } from "./useServiceTemplateScheduleOccurrenceBulkActions";
import {
    formatServiceTemplateScheduleOccurrenceCalendarDate,
    formatServiceTemplateScheduleOccurrenceDate,
} from "./formatServiceTemplateScheduleOccurrenceDate";
import { getMemberLabel } from "./serviceTemplateScheduleMemberLabel";
import { ServiceTemplateScheduleOccurrenceStatusFilter } from "./ServiceTemplateScheduleOccurrenceStatusFilter";
import styles from "./ServiceTemplateScheduleOccurrencesDrawer.module.scss";
import { Tooltip } from "@design-system/Tooltip/Tooltip";

const AVAILABLE_FILTERS: FilterTabs[] = [FilterTabs.APPLICATION_DATE];
const PAGE_SIZE = 50;

interface Props {
    open: boolean;
    onClose: () => void;
    schedule: ServiceTemplateSchedule | null;
}

export const ServiceTemplateScheduleOccurrencesDrawer = ({
    open,
    onClose,
    schedule,
}: Props) => {
    const { organizationId } = useScope();
    const { toast: successToast, error: errorToast } = useToaster();
    const openWorkRow = useOpenWorkRow();
    const queryClient = useQueryClient();

    // Matches the backend default order (INDEX_ASC). Clearing an active sort
    // returns to this rather than an unsorted / insertion order.
    const [sort, setSort] =
        useState<ServiceTemplateScheduleOccurrencesSortValue>(
            ServiceTemplateScheduleOccurrencesSortValue.INDEX_ASC,
        );
    const handleSortChange = useCallback(
        (newSort: ServiceTemplateScheduleOccurrencesSortValue) =>
            setSort(newSort),
        [],
    );
    const handleSortRemove = useCallback(
        () => setSort(ServiceTemplateScheduleOccurrencesSortValue.INDEX_ASC),
        [],
    );

    const {
        data: occurrences,
        isLoading,
        isError,
    } = useServiceTemplateScheduleOccurrences({
        organizationId,
        scheduleId: schedule?.id,
        sort,
        enabled: open,
    });

    const scheduleWorkspaceId = schedule?.workspaceId ?? null;

    // Whether this schedule's recurrence itself is paused — surfaced on the
    // status cell so not-yet-applied occurrences read "Paused" (PRO-10574).
    const isRecurrencePaused =
        schedule?.status === ServiceTemplateRecurrenceStatus.PAUSED;

    // Whether the schedule's close template has been archived — needs
    // `includeArchived` since the default list excludes archived templates.
    // Same rationale as `isRecurrencePaused` above.
    const { data: closeTemplates = [] } = useCloseTemplates({
        includeArchived: true,
        enabled: open && !!schedule?.templateId,
    });
    const isTemplateArchived = useMemo(
        () =>
            closeTemplates.some(
                (t) => t.id === schedule?.templateId && t.archivedAt != null,
            ),
        [closeTemplates, schedule?.templateId],
    );

    const templateEstimateStats = useMemo(() => {
        const template = closeTemplates.find(
            (t) => t.id === schedule?.templateId,
        );
        if (!template) {
            return undefined;
        }
        return {
            totalEstimatedTime: template.totalEstimatedTime,
            estimatedTimeByRole: template.estimatedTimeByRole,
        };
    }, [closeTemplates, schedule?.templateId]);

    // Assignable preparer/reviewer/manager options: any organization member
    // (matches backend role validation and the schedule Team defaults step —
    // neither requires workspace access for these roles).
    const { data: assignableMembers = [] } = useCurrentOrganizationMembers({
        enabled: open,
    });
    const { data: workspaceDetails } = useWorkspaceDetails(
        open ? scheduleWorkspaceId : null,
    );

    // Assignable client options: workspace-level assignments, which include
    // members without organization access (mirrors ApplyCloseEntityClientCell).
    const workspaceMembers = useMemo(
        () => workspaceDetails?.assignments ?? [],
        [workspaceDetails],
    );

    const { entities: workspaceEntities } = useWorkspaceEntities(
        open ? scheduleWorkspaceId : null,
    );

    const clientInfo = useMemo(
        () => workspaceDetails?.workspace ?? null,
        [workspaceDetails],
    );
    const entityInfo = useMemo(
        () =>
            workspaceEntities.find(
                (entity) => entity.uuid === schedule?.entityUuid,
            ) ?? null,
        [workspaceEntities, schedule?.entityUuid],
    );

    const rows = useMemo<ServiceTemplateScheduleOccurrenceRow[]>(
        () =>
            (occurrences ?? []).map((occurrence) => ({
                id: occurrence.id,
                index: occurrence.occurrenceIndex,
                applicationDate: occurrence.applicationDate,
                serviceStartDate: occurrence.serviceStartDate,
                serviceEndDate: occurrence.serviceEndDate,
                workStartDate: occurrence.workStartDate,
                workEndDate: occurrence.workEndDate,
                // Occurrence rows only store per-occurrence role overrides
                // (mostly null); the resolved fields fall back to the
                // recurrence's default roles, matching what applying the
                // occurrence will actually use.
                preparerId: occurrence.resolvedPreparerId,
                reviewerId: occurrence.resolvedReviewerId,
                managerId: occurrence.resolvedManagerId,
                clientId: occurrence.resolvedClientId,
                status: occurrence.status,
                closeId: occurrence.closeId,
                workspaceId: occurrence.workspaceId,
            })),
        [occurrences],
    );

    const [search, setSearch] = useState("");

    // No status selected by default (i.e. show occurrences of every status).
    const [statusFilter, setStatusFilter] = useState<
        ServiceTemplateRecurrenceOccurrenceStatus[]
    >([]);

    // Application date defaults to today when opening the drawer, so only
    // future (not-yet-applied) occurrences are shown by default.
    const [filters, setFilters] = useState<FiltersTypes>(() => ({
        applicationDateStart: startOfDay(new Date()),
    }));
    const handleFiltersChange = useCallback<FiltersChangeFn>(
        (patch) => setFilters((current) => ({ ...current, ...patch })),
        [],
    );

    const [currentPage, setCurrentPage] = useState(1);
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    const clearSelection = useCallback(() => setRowSelection({}), []);

    // Reset the search and filters when the drawer closes or switches
    // schedules so a stale filter never carries over into the next open
    // (which could hide all rows). Page/selection reset via the effect below.
    useEffect(() => {
        setSearch("");
        setStatusFilter([]);
        setFilters({ applicationDateStart: startOfDay(new Date()) });
    }, [open, schedule?.id]);

    // Filter/search changes shrink the result set — jump back to page 1 so
    // the user isn't stranded on an empty page.
    useEffect(() => {
        setCurrentPage(1);
        setRowSelection({});
    }, [search, statusFilter, filters]);

    // Maps assignable-member ids to display labels so search can match on
    // assignee names, not just raw ids.
    const assignableMemberLabelById = useMemo(() => {
        const map = new Map<number, string>();
        assignableMembers.forEach((member) => {
            map.set(member.user.id, getMemberLabel(member));
        });
        return map;
    }, [assignableMembers]);

    const workspaceMemberLabelById = useMemo(() => {
        const map = new Map<number, string>();
        workspaceMembers.forEach((member) => {
            map.set(member.user.id, getMemberLabel(member));
        });
        return map;
    }, [workspaceMembers]);

    // Search matches on assignee names (preparer/reviewer/manager/client) or
    // any of the occurrence's dates. Status and application-date filters are
    // applied first so search operates on the already-narrowed set.
    const filteredRows = useMemo(() => {
        const { applicationDateStart, applicationDateEnd } = filters;

        const statusAndDateFiltered = rows.filter((row) => {
            if (
                statusFilter.length > 0 &&
                (!row.status || !statusFilter.includes(row.status))
            ) {
                return false;
            }

            if (applicationDateStart || applicationDateEnd) {
                const applicationDate = parseISO(row.applicationDate);
                if (
                    applicationDateStart &&
                    applicationDate < applicationDateStart
                ) {
                    return false;
                }
                if (
                    applicationDateEnd &&
                    applicationDate > applicationDateEnd
                ) {
                    return false;
                }
            }

            return true;
        });

        const normalizedSearch = search.trim().toLowerCase();
        if (!normalizedSearch) {
            return statusAndDateFiltered;
        }
        return statusAndDateFiltered.filter((row) => {
            const searchable = [
                row.preparerId != null
                    ? assignableMemberLabelById.get(row.preparerId)
                    : null,
                row.reviewerId != null
                    ? assignableMemberLabelById.get(row.reviewerId)
                    : null,
                row.managerId != null
                    ? assignableMemberLabelById.get(row.managerId)
                    : null,
                row.clientId != null
                    ? workspaceMemberLabelById.get(row.clientId)
                    : null,
                ...[
                    row.applicationDate,
                    row.serviceStartDate,
                    row.serviceEndDate,
                    row.workStartDate,
                    row.workEndDate,
                ].flatMap((date) => [
                    formatServiceTemplateScheduleOccurrenceDate(date),
                    formatServiceTemplateScheduleOccurrenceCalendarDate(date),
                ]),
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
            return searchable.includes(normalizedSearch);
        });
    }, [
        search,
        rows,
        statusFilter,
        filters,
        assignableMemberLabelById,
        workspaceMemberLabelById,
    ]);

    const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
    const safePage = Math.min(currentPage, totalPages);
    const pageRows = useMemo(() => {
        const start = (safePage - 1) * PAGE_SIZE;
        return filteredRows.slice(start, start + PAGE_SIZE);
    }, [filteredRows, safePage]);

    const handlePageChange = useCallback((page: number) => {
        setRowSelection({});
        setCurrentPage(page);
    }, []);

    const rowsById = useMemo(
        () => new Map(filteredRows.map((row) => [row.id, row])),
        [filteredRows],
    );

    const selectedRows = useMemo(
        () =>
            Object.entries(rowSelection)
                .filter(([, selected]) => selected)
                .map(([id]) => rowsById.get(id))
                .filter(
                    (row): row is ServiceTemplateScheduleOccurrenceRow => !!row,
                ),
        [rowSelection, rowsById],
    );

    const selectableDrawerView = useMemo(
        () => ({
            ...serviceTemplateScheduleOccurrencesDrawerView,
            options: {
                ...serviceTemplateScheduleOccurrencesDrawerView.options,
                selection: true,
                bulkActions: true,
            },
        }),
        [],
    );

    const {
        handleOpenBulkActions,
        showDeleteConfirm,
        setShowDeleteConfirm,
        handleBulkDeleteConfirm,
        deleteEligibleCount,
        isBulkActionPending,
    } = useServiceTemplateScheduleOccurrenceBulkActions({
        organizationId,
        selectedRows,
        clearSelection,
        assignableMembers,
        workspaceMembers,
    });

    const { mutateAsync: applyOccurrence, isPending: isApplying } =
        useApplyOccurrenceNowMutation();
    const { mutateAsync: skipOccurrence, isPending: isSkipping } =
        useSkipOccurrenceMutation();
    const { mutateAsync: unskipOccurrence, isPending: isUnskipping } =
        useUnskipOccurrenceMutation();
    const { mutateAsync: updateRoleOverride, isPending: isUpdatingRole } =
        useUpdateOccurrenceRoleOverrideMutation();
    const { mutateAsync: deleteClose, isPending: isDeletingClose } =
        useDeleteCloseMutation();
    // Tracks the unskip -> apply sequence in `handleApplySkippedOccurrence`.
    // React Query's `isUnskipping`/`isApplying` both momentarily drop to
    // `false` between the two awaits, which would flicker the dropdown items
    // back to enabled for one render — this state stays true across the whole
    // sequence to keep them disabled.
    const [isApplyingSkippedOccurrence, setIsApplyingSkippedOccurrence] =
        useState(false);
    const isMutatingOccurrence =
        isApplying ||
        isSkipping ||
        isUnskipping ||
        isUpdatingRole ||
        isBulkActionPending ||
        isDeletingClose ||
        isApplyingSkippedOccurrence;
    const isMutatingOccurrenceRef = useRef(false);

    const runOccurrenceAction = useCallback(
        async <TData,>({
            occurrenceId,
            mutate,
            successMessage,
            errorMessage,
        }: {
            occurrenceId: string;
            mutate: (variables: {
                organizationId: string;
                occurrenceId: string;
            }) => Promise<TData>;
            successMessage: string;
            errorMessage: string;
        }) => {
            if (!organizationId || isMutatingOccurrenceRef.current) {
                return;
            }
            isMutatingOccurrenceRef.current = true;
            try {
                await mutate({ organizationId, occurrenceId });
                successToast(successMessage);
            } catch (e) {
                errorToast(e instanceof Error ? e.message : errorMessage);
            } finally {
                isMutatingOccurrenceRef.current = false;
            }
        },
        [organizationId, successToast, errorToast],
    );

    const handleUpdateRoleOverride = useCallback(
        async ({
            occurrenceId,
            field,
            value,
        }: {
            occurrenceId: string;
            field: ServiceTemplateScheduleOccurrenceRoleField;
            value: number | null;
        }) => {
            if (!organizationId || isMutatingOccurrenceRef.current) {
                return;
            }
            isMutatingOccurrenceRef.current = true;
            try {
                await updateRoleOverride({
                    organizationId,
                    occurrenceId,
                    override: { [field]: value },
                });
                successToast("Role assignment updated");
            } catch (e) {
                errorToast(
                    e instanceof Error
                        ? e.message
                        : "Failed to update role assignment",
                );
            } finally {
                isMutatingOccurrenceRef.current = false;
            }
        },
        [organizationId, updateRoleOverride, successToast, errorToast],
    );

    const handleDeleteClose = useCallback(
        async (closeId: string) => {
            if (!organizationId || isMutatingOccurrenceRef.current) {
                return;
            }
            isMutatingOccurrenceRef.current = true;
            try {
                await deleteClose(closeId);
                void queryClient.invalidateQueries({
                    queryKey:
                        serviceTemplateScheduleQueryKeys.organization(
                            organizationId,
                        ),
                });
                successToast("Work deleted");
            } catch (e) {
                errorToast(
                    e instanceof Error ? e.message : "Failed to delete work",
                );
            } finally {
                isMutatingOccurrenceRef.current = false;
            }
        },
        [deleteClose, organizationId, queryClient, successToast, errorToast],
    );

    // A skipped occurrence's shell is still `SKIPPED`, and `applyOccurrenceNow`
    // only accepts `SCHEDULED` shells — so applying a skipped occurrence right
    // now is unskip (SKIPPED -> SCHEDULED) immediately followed by apply
    // (SCHEDULED -> APPLIED), surfaced to the user as a single "Apply" action.
    const handleApplySkippedOccurrence = useCallback(
        async (occurrenceId: string) => {
            if (!organizationId || isMutatingOccurrenceRef.current) {
                return;
            }
            isMutatingOccurrenceRef.current = true;
            setIsApplyingSkippedOccurrence(true);
            // Once the unskip succeeds the occurrence is already SCHEDULED, so
            // a subsequent apply failure must not report "nothing happened" —
            // surface that the occurrence is back on the schedule instead.
            let didUnskip = false;
            try {
                await unskipOccurrence({ organizationId, occurrenceId });
                didUnskip = true;
                await applyOccurrence({ organizationId, occurrenceId });
                successToast("Occurrence applied");
            } catch (e) {
                if (didUnskip) {
                    errorToast(
                        "Couldn't apply the occurrence — it's back on the schedule. Try again",
                    );
                } else {
                    errorToast(
                        e instanceof Error
                            ? e.message
                            : "Failed to apply occurrence",
                    );
                }
            } finally {
                isMutatingOccurrenceRef.current = false;
                setIsApplyingSkippedOccurrence(false);
            }
        },
        [
            organizationId,
            unskipOccurrence,
            applyOccurrence,
            successToast,
            errorToast,
        ],
    );

    const callbacks = useMemo<ServiceTemplateScheduleOccurrenceCallbacks>(
        () => ({
            onApply: (occurrenceId) =>
                runOccurrenceAction({
                    occurrenceId,
                    mutate: applyOccurrence,
                    successMessage: "Occurrence applied",
                    errorMessage: "Failed to apply occurrence",
                }),
            onSkip: (occurrenceId) =>
                runOccurrenceAction({
                    occurrenceId,
                    mutate: skipOccurrence,
                    successMessage: "Occurrence skipped",
                    errorMessage: "Failed to skip occurrence",
                }),
            onUnskip: (occurrenceId) =>
                runOccurrenceAction({
                    occurrenceId,
                    mutate: unskipOccurrence,
                    successMessage: "Skip undone",
                    errorMessage: "Failed to undo skip",
                }),
            onApplySkipped: handleApplySkippedOccurrence,
            onUpdateRoleOverride: handleUpdateRoleOverride,
            onOpenWorkRow: openWorkRow,
            onDeleteClose: handleDeleteClose,
        }),
        [
            applyOccurrence,
            handleApplySkippedOccurrence,
            handleUpdateRoleOverride,
            runOccurrenceAction,
            skipOccurrence,
            unskipOccurrence,
            openWorkRow,
            handleDeleteClose,
        ],
    );

    const tableMeta = useMemo<
        ViewTableMeta<
            ServiceTemplateScheduleOccurrenceCallbacks,
            ServiceTemplateScheduleOccurrenceTableOptions
        >
    >(
        () => ({
            callbacks,
            options: {
                isMutatingOccurrence,
                assignableMembers,
                workspaceMembers,
                isTemplateArchived,
                isRecurrencePaused,
                templateEstimateStats,
            },
        }),
        [
            callbacks,
            isMutatingOccurrence,
            assignableMembers,
            workspaceMembers,
            isTemplateArchived,
            isRecurrencePaused,
            templateEstimateStats,
        ],
    );

    // Client + entity breadcrumb shown next to the drawer title, mirroring
    // the CoA applied-entities / apply-template drawer headers. Falls back to
    // the plain title until the client resolves. The entity segment is only
    // appended when the schedule's entity is present, so a resolved client is
    // never dropped just because its entity is missing.
    const headerAddonLeft = useMemo(() => {
        if (!clientInfo) {
            return (
                <Breadcrumbs>
                    <BreadcrumbItem current>
                        Upcoming occurrences
                    </BreadcrumbItem>
                </Breadcrumbs>
            );
        }
        const clientIcon = (
            <WorkspaceAvatar workspace={clientInfo} size="xs" aria-hidden />
        );
        return (
            <Breadcrumbs>
                <BreadcrumbItem>Upcoming occurrences</BreadcrumbItem>
                {entityInfo ? (
                    <>
                        <BreadcrumbItem leftIcon={clientIcon}>
                            {clientInfo.name}
                        </BreadcrumbItem>
                        <BreadcrumbItem
                            current
                            leftIcon={
                                <EntityAvatar
                                    entity={entityInfo}
                                    size="xs"
                                    aria-hidden
                                />
                            }
                        >
                            {entityInfo.name ?? "-"}
                        </BreadcrumbItem>
                    </>
                ) : (
                    <BreadcrumbItem current leftIcon={clientIcon}>
                        {clientInfo.name}
                    </BreadcrumbItem>
                )}
            </Breadcrumbs>
        );
    }, [clientInfo, entityInfo]);

    const renderContent = () => {
        if (isLoading) {
            return <Loader />;
        }
        if (isError) {
            return (
                <EmptyState
                    header="Failed to load occurrences"
                    body="Something went wrong while loading upcoming occurrences. Please try again."
                    ctaText={undefined}
                />
            );
        }
        const hasPagination = totalPages > 1;
        const hasSelection = selectedRows.length > 0;

        return (
            <ServiceTemplateScheduleOccurrencesTable
                rows={pageRows}
                view={selectableDrawerView}
                tableMeta={tableMeta}
                sort={sort}
                onSortChange={handleSortChange}
                onSortRemove={handleSortRemove}
                rowSelection={rowSelection}
                onRowSelectionChange={setRowSelection}
                enableRowSelection
                footer={
                    hasPagination || hasSelection ? (
                        <CommandIsland
                            sticky
                            className={
                                commandIslandStyles.bulkActionsStickyFooter
                            }
                        >
                            <HStack gap={8}>
                                {hasPagination ? (
                                    <Pagination
                                        pageCount={totalPages}
                                        currentPage={safePage}
                                        onPageChange={handlePageChange}
                                        size="md"
                                        buttonsPosition="left"
                                    />
                                ) : null}
                                {hasSelection ? (
                                    <BulkSelectionControl
                                        size="md"
                                        selectedCount={selectedRows.length}
                                        onClearSelection={clearSelection}
                                    />
                                ) : null}
                            </HStack>

                            {hasSelection ? (
                                <Tooltip content="Open bulk actions">
                                    <Button
                                        variant="primary"
                                        size="md"
                                        rightIcon={
                                            <span data-slot-box="">
                                                <Shortcut keys="mod+k" />
                                            </span>
                                        }
                                        onClick={handleOpenBulkActions}
                                    >
                                        Actions
                                    </Button>
                                </Tooltip>
                            ) : null}
                        </CommandIsland>
                    ) : undefined
                }
            />
        );
    };

    return (
        <>
            <Drawer
                open={open}
                onClose={onClose}
                size="fullScreenWithNav"
                headerAddonLeft={headerAddonLeft}
                noPadding
            >
                {open && schedule ? (
                    <div className={styles.container}>
                        <TablePageHeader
                            search={search}
                            onSearchChange={setSearch}
                            searchPlaceholder="Search by assignee or date..."
                            isFullPage
                            filters={filters}
                            onFiltersChange={handleFiltersChange}
                            availableFilters={AVAILABLE_FILTERS}
                            defaultTab={FilterTabs.APPLICATION_DATE}
                        >
                            <ServiceTemplateScheduleOccurrenceStatusFilter
                                selectedStatuses={statusFilter}
                                onChange={setStatusFilter}
                            />
                        </TablePageHeader>
                        {renderContent()}
                    </div>
                ) : null}
            </Drawer>

            <ConfirmationModal
                show={showDeleteConfirm}
                title={`Delete ${deleteEligibleCount} ${pluralize("occurrence", deleteEligibleCount)}?`}
                question="This will permanently delete the selected occurrence(s). This action cannot be undone."
                yes="Delete"
                yesBtnVariant="destructive"
                onConfirm={handleBulkDeleteConfirm}
                onReject={() => setShowDeleteConfirm(false)}
                onHide={() => setShowDeleteConfirm(false)}
            />
        </>
    );
};
